import "./WeekView.css";
import Style from "../ResourceMonitor.module.scss";
import ShiftCell from "../../../../components/shiftsCell/ShiftCell";
import CalendarHeadings from "../../../../components/resourceHeader/CalendarHeadings";
import HourlyEmptyCell from "../../../../components/hourlyEmptyCell/HourlyEmptyCell";
import HourlyShiftsCell from "../../../../components/hourlyShiftsCell/HourlyShiftsCell";
import {
  Avatar,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  TableCellLayout,
  Tooltip,
} from "@fluentui/react-components";
import { useContext, useState, memo, useCallback, useRef, useMemo } from "react";
import { ResourcePlannerContext } from "../ResourcePlannerContext";
import { Rnd } from "react-rnd";
import HoverCard from "../../../../components/hoverCard/HoverCard";
import { MeatBallsMenuIcon } from "../../../../modules/default-svg";
import { PopoverMenu } from "../../../../components/controls/table/Table";
import { ReminderModal as CreateReminder } from "../../../../components/controls/modal/reminder/Reminder";

const CalendarLayout = memo(({
  userData,
  calendarData,
  headerDate,
  handleClick,
  handleEditModel,
  type = false,
  dashboard = false,
}) => {
  const [popoverOpen, setPopoverOpen] = useState({});
  const [closePopover, setClosePopover] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [showReminderModal, setShowReminderModal] = useState(false);

  const { filterStartDate, detail, setDetail } = useContext(ResourcePlannerContext);

  // Refs for scroll sync
  const resourceWrapperRef = useRef(null);
  const calendarBodyRef = useRef(null);

  const handlePopoverOpen = useCallback((rowId) => {
    setPopoverOpen(rowId);
  }, []);

  const handleModal = useCallback((item) => {
    setClosePopover(false);
    setSelectedItem(item);
  }, []);

  const handleShowReminder = useCallback((item) => {
    setPopoverOpen(false);
    setShowReminderModal(true);
    localStorage.setItem("selectedRow", JSON.stringify(item));
  }, []);

  const handleUserCardClick = useCallback((index) => {
    setDetail({
      userIndex: index,
      ind: index,
    });
  }, [setDetail]);

  // Optimized scroll sync handlers
  const handleResourceScroll = useCallback((e) => {
    if (calendarBodyRef.current) {
      calendarBodyRef.current.scrollTop = e.target.scrollTop;
    }
  }, []);

  const handleCalendarScroll = useCallback((e) => {
    if (resourceWrapperRef.current) {
      resourceWrapperRef.current.scrollTop = e.target.scrollTop;
    }
  }, []);

  // Memoize menu component
  const menu = useMemo(() => (
    <PopoverMenu
      closePopover={closePopover}
      setClosePopover={setClosePopover}
      selectedItem={selectedItem}
      setReminderModal={handleShowReminder}
    />
  ), [closePopover, selectedItem, handleShowReminder]);

  return (
    <div
      className={Style.MonitoringCalendarScroll}
      style={{
        height: dashboard ? (!dashboard?.autoWidth ? "226px" : "auto") : null,
        overflow: dashboard ? (!dashboard?.autoWidth ? "hidden" : "auto") : null,
      }}
    >
      <Rnd
        default={{ width: 188 }}
        minWidth={188}
        maxWidth={500}
        bounds="window"
        enableResizing={{ right: true }}
        style={{
          position: "sticky",
          left: 0,
          zIndex: 2,
          cursor: "pointer",
        }}
      >
        <div
          ref={resourceWrapperRef}
          className={Style.ResCardWrapper}
          style={{
            height: "100%",
            overflowY: "auto",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
          }}
          onScroll={handleResourceScroll}
        >
          <div className={Style.ResHeader}>Resources</div>
          <div className="d-flex flex-column">
            {userData.map((item, index) => (
              <div
                key={item.id}
                className={`${Style.ResUserCard} ${
                  index === detail?.userIndex ? Style.selected : Style.unSelected
                }`}
                onClick={() => handleUserCardClick(index)}
              >
                <div
                  className={`d-flex align-items-center gap-1 ${Style.shrinkedText}`}
                >
                  <div className="custom-row-resource-planner-header">
                    <div className="custom-row-resource-planner">
                      <HoverCard
                        HoverCardTrigger={
                          <Avatar
                            color="colorful"
                            size={24}
                            name={item.name}
                            image={{ src: item.imageUrl || "" }}
                          />
                        }
                        AvatarId={item.id}
                        AvatarName={item.name}
                        AvatarImage={item.imageUrl}
                        AvatarDesignation={item.title}
                        AvatarEmail={item.email}
                        AvatarPhone={item.phoneNumber}
                        AvatarIsExternal={item.isExternal}
                      />
                      <p className="text-sm-400 textColor1 ms-6 text-truncate userName">
                        {item.name}
                      </p>
                    </div>
                    <div>
                      <Popover
                        open={popoverOpen === item.id}
                        onOpenChange={(isOpen) =>
                          isOpen && handlePopoverOpen(item.id)
                        }
                        positioning="below-start"
                      >
                        <PopoverTrigger disableButtonEnhancement>
                          <TableCellLayout
                            media={<MeatBallsMenuIcon />}
                            onClick={() => handleModal(item)}
                          />
                        </PopoverTrigger>
                        <PopoverSurface
                          tabIndex={-1}
                          className="listingPopoverWrapper"
                        >
                          {menu}
                        </PopoverSurface>
                      </Popover>
                    </div>
                  </div>
                  {!!filterStartDate && (
                    <Tooltip
                      content={`Capacity: ${
                        ((item.workLoad / item.totalHours) * 100 || 0).toFixed(2)
                      }% Utilized`}
                      relationship="label"
                      withArrow
                      positioning="after"
                    >
                      <span className="CapacityWeekly">
                        {item.workLoad}/{item.totalHours}hrs
                      </span>
                    </Tooltip>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Rnd>
      <div
        ref={calendarBodyRef}
        className={Style.calendarBody}
        style={{
          flex: 1,
          overflow: "auto",
          height: dashboard ? (!dashboard?.autoWidth ? "226px" : "auto") : "auto",
        }}
        onScroll={handleCalendarScroll}
      >
        <CalendarHeadings daysOfWeek={headerDate} type={type} />
        {!type ? (
          <div className="d-flex flex-column">
            {calendarData.map((day, index) => (
              <div className="d-flex" key={`day-${index}`}>
                {Array.isArray(day) &&
                  day.map((item, ind) => (
                    <ShiftCell
                      key={`shift-${index}-${ind}`}
                      dataList={item}
                      handleClick={handleClick}
                      handleEditModel={handleEditModel}
                      pointer={index}
                      ind={ind}
                    />
                  ))}
              </div>
            ))}
          </div>
        ) : (
          <div className="d-flex flex-column">
            {calendarData.map((item, ind) => {
              let skip = 0;
              return (
                <div className="d-flex" key={`hour-row-${ind}`}>
                  {headerDate.map((shift, index) => {
                    const key = `${shift.date}${shift.name.split(" ").join("")}`;
                    
                    if (skip > 0 && !item[key]) {
                      skip--;
                      return null;
                    } else if (!item[key]) {
                      return <HourlyEmptyCell key={`empty-${ind}-${index}`} index={ind} />;
                    } else {
                      skip = Object.keys(item[key]).length - 1;
                      return (
                        <HourlyShiftsCell
                          key={`shift-${ind}-${index}`}
                          item={item[key]}
                          handleClick={handleClick}
                          index={ind}
                          identifier={key}
                        />
                      );
                    }
                  })}
                </div>
              );
            })}
          </div>
        )}
      </div>
      {showReminderModal && (
        <CreateReminder
          setIsModalOpen={setShowReminderModal}
          type={"User"}
          selectedTag={selectedItem}
        />
      )}
    </div>
  );
});

CalendarLayout.displayName = 'CalendarLayout';

export default CalendarLayout;
